import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Building2,
  Upload,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Plus,
  Eye,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { format, isPast, differenceInDays } from "date-fns";
import { fr } from "date-fns/locale";
import PortalDocumentUploadDialog from "@/components/subcontractors/PortalDocumentUploadDialog";
import { SEO } from "@/components/seo/SEO";

interface SubcontractorInfo {
  id: string;
  company_name: string;
  contact_name: string | null;
  contact_email: string | null;
}

interface PortalDocument {
  id: string;
  document_type: string;
  label: string | null;
  valid_from: string | null;
  expires_at: string | null;
  status: string;
  created_at: string;
  file_name: string | null;
  file_path: string | null;
}

const DOCUMENT_TYPES = [
  { type: "kbis", label: "Kbis / Extrait K" },
  { type: "urssaf_certificate", label: "Attestation URSSAF" },
  { type: "rc_pro_insurance", label: "Assurance RC Pro" },
  { type: "fiscal_regularity", label: "Régularité fiscale" },
  { type: "decennial_insurance", label: "Assurance décennale" },
  { type: "professional_card", label: "Carte professionnelle" },
  { type: "qualibat", label: "Certification Qualibat" },
  { type: "rib", label: "RIB" },
  { type: "statutes", label: "Statuts" },
  { type: "other", label: "Autre document" },
];

const SubcontractorPortal = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [subcontractor, setSubcontractor] = useState<SubcontractorInfo | null>(null);
  const [documents, setDocuments] = useState<PortalDocument[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!token) {
      setError("Lien invalide");
      setLoading(false);
      return;
    }

    try {
      // Fetch subcontractor info
      const { data: subData, error: subError } = await supabase.rpc(
        "get_subcontractor_by_token",
        { p_token: token }
      );

      if (subError) throw subError;

      if (!subData || subData.length === 0) {
        setError("Ce lien d'accès n'est pas valide ou a expiré");
        setLoading(false);
        return;
      }

      setSubcontractor(subData[0]);

      // Fetch documents
      const { data: docsData, error: docsError } = await supabase.rpc(
        "get_subcontractor_documents_by_token",
        { p_token: token }
      );

      if (docsError) throw docsError;
      setDocuments(docsData || []);
    } catch (err) {
      console.error("Error fetching portal data:", err);
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getDocumentByType = (type: string) => {
    return documents.find((d) => d.document_type === type);
  };

  const getStatusInfo = (doc: PortalDocument | undefined) => {
    if (!doc) {
      return {
        status: "missing",
        color: "text-muted-foreground",
        bgColor: "bg-muted",
        icon: Plus,
        label: "À fournir",
      };
    }

    if (!doc.expires_at) {
      if (doc.status === "pending_verification") {
        return {
          status: "pending",
          color: "text-amber-600",
          bgColor: "bg-amber-500/10",
          icon: Clock,
          label: "En attente de vérification",
        };
      }
      return {
        status: "valid",
        color: "text-emerald-600",
        bgColor: "bg-emerald-500/10",
        icon: CheckCircle2,
        label: "Validé",
      };
    }

    const expiresAt = new Date(doc.expires_at);
    const daysUntilExpiry = differenceInDays(expiresAt, new Date());

    if (isPast(expiresAt)) {
      return {
        status: "expired",
        color: "text-destructive",
        bgColor: "bg-destructive/10",
        icon: AlertTriangle,
        label: "Expiré - À renouveler",
      };
    }

    if (daysUntilExpiry <= 30) {
      return {
        status: "expiring",
        color: "text-amber-600",
        bgColor: "bg-amber-500/10",
        icon: Clock,
        label: `Expire dans ${daysUntilExpiry} jours`,
      };
    }

    if (doc.status === "pending_verification") {
      return {
        status: "pending",
        color: "text-amber-600",
        bgColor: "bg-amber-500/10",
        icon: Clock,
        label: "En attente de vérification",
      };
    }

    return {
      status: "valid",
      color: "text-emerald-600",
      bgColor: "bg-emerald-500/10",
      icon: CheckCircle2,
      label: "Validé",
    };
  };

  const handleUploadClick = (docType: string) => {
    setSelectedDocType(docType);
    setUploadDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement du portail...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <SEO
          title="Portail inaccessible | EasyFinder"
          description="Ce portail prestataire n'est pas accessible"
          noIndex={true}
        />
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Accès refusé</h1>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const validDocs = documents.filter(
    (d) => d.status === "valid" && (!d.expires_at || !isPast(new Date(d.expires_at)))
  );
  const pendingDocs = documents.filter((d) => d.status === "pending_verification");
  const expiredDocs = documents.filter(
    (d) => d.expires_at && isPast(new Date(d.expires_at))
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      <SEO
        title={`Portail ${subcontractor?.company_name} | EasyFinder`}
        description="Déposez et gérez vos documents de conformité"
        noIndex={true}
      />

      {/* Header */}
      <header className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-semibold">{subcontractor?.company_name}</h1>
              <p className="text-sm text-muted-foreground">Portail de dépôt des documents</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container max-w-4xl mx-auto px-4 py-8">
        {/* Welcome Card */}
        <Card className="mb-6 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-primary/10 shrink-0">
                <ShieldCheck className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-lg mb-1">
                  Bienvenue sur votre espace de conformité
                </h2>
                <p className="text-muted-foreground text-sm">
                  Déposez ici vos documents obligatoires. Ils seront vérifiés par notre équipe 
                  et vous serez notifié de leur validation. Pensez à mettre à jour vos documents 
                  avant leur expiration.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-emerald-600">{validDocs.length}</div>
              <p className="text-xs text-muted-foreground">Validés</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-amber-600">{pendingDocs.length}</div>
              <p className="text-xs text-muted-foreground">En attente</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-destructive">{expiredDocs.length}</div>
              <p className="text-xs text-muted-foreground">Expirés</p>
            </CardContent>
          </Card>
        </div>

        {/* Documents List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Documents de conformité
            </CardTitle>
            <CardDescription>
              Cliquez sur un document pour le déposer ou le mettre à jour
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {DOCUMENT_TYPES.map(({ type, label }) => {
                const doc = getDocumentByType(type);
                const statusInfo = getStatusInfo(doc);
                const StatusIcon = statusInfo.icon;

                return (
                  <div
                    key={type}
                    className={cn(
                      "flex items-center justify-between p-4 rounded-lg border transition-colors",
                      !doc && "border-dashed hover:border-primary/50 hover:bg-primary/5 cursor-pointer",
                      doc && "bg-muted/30"
                    )}
                    onClick={() => !doc && handleUploadClick(type)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn("p-2 rounded-lg", statusInfo.bgColor)}>
                        <StatusIcon className={cn("w-4 h-4", statusInfo.color)} />
                      </div>
                      <div>
                        <p className="font-medium">{label}</p>
                        <p className={cn("text-sm", statusInfo.color)}>
                          {statusInfo.label}
                          {doc?.expires_at && statusInfo.status === "valid" && (
                            <span className="text-muted-foreground ml-1">
                              (exp. {format(new Date(doc.expires_at), "dd MMM yyyy", { locale: fr })})
                            </span>
                          )}
                        </p>
                        {doc?.file_name && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[200px]">
                            {doc.file_name}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {doc ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUploadClick(type);
                          }}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Remplacer
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => handleUploadClick(type)}>
                          <Upload className="w-4 h-4 mr-1" />
                          Déposer
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          Portail sécurisé propulsé par EasyFinder
        </p>
      </main>

      {token && (
        <PortalDocumentUploadDialog
          open={uploadDialogOpen}
          onOpenChange={setUploadDialogOpen}
          token={token}
          documentType={selectedDocType}
          onSuccess={fetchData}
        />
      )}
    </div>
  );
};

export default SubcontractorPortal;
